import {
	Box,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	tableCellClasses,
} from '@mui/material'
import { ReactComponent as CheckupIcon } from 'assets/checkup-icon.svg'
import { ReactComponent as ArrowNext } from 'assets/fill_back_arrow.svg'
import { getYearsOld, morphForWords } from 'components/shared/processes'
import {
	ColumnTypes,
	IFiltersLamenessTable,
	InspectionStatus,
	LamenessColumnKey,
	PositionHoof,
	TreatmentStatus,
} from 'models'
import { ICowsList } from 'models/ICattlesData'
import moment from 'moment'
import { useEffect, useRef } from 'react'
import { fetchDiagnosesList } from 'store/cattlesSlice'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import TableSkeleton from 'ui/skeleton/TableSkeleton'
import TooltipHover from 'ui/TooltipHover'
import { TypographyPrimary } from 'ui/typography/TypographyPrimary'
import { TypographySecondary } from 'ui/typography/TypographySecondary'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'
import { TypographySecondaryBold16 } from 'ui/typography/TypographySecondaryBold16'
import { openCheckupInNewTab, openInNewBrowserTab } from 'utils/checkupWindow'
import { getTranslationStatus } from './processes'

type LamenesTableProps = {
	cattlesInfo: ICowsList[]
	fetchingCowsList: boolean
	activeFilters: IFiltersLamenessTable
	getColumn: (key: LamenessColumnKey) => ColumnTypes | undefined
}

const COLUMN_WIDTHS: Record<LamenessColumnKey, number> = {
	[LamenessColumnKey.TREATMENT_PLAN]: 160,
	[LamenessColumnKey.DIAGNOSIS]: 150,
	[LamenessColumnKey.TREATMENT_STATUS]: 140,
	[LamenessColumnKey.INSPECTION_DATE]: 130,
	[LamenessColumnKey.LACTATION_DAY]: 100,
	[LamenessColumnKey.COW_AGE]: 120,
	[LamenessColumnKey.GROUP]: 120,
	[LamenessColumnKey.LACTATION_STATUS]: 130,
	[LamenessColumnKey.FATNESS]: 120,
	[LamenessColumnKey.EXAMINER]: 120,
}

const FIXED_COLUMNS_WIDTH = 130 + 160 + 113

const HOOF_ORDER: PositionHoof[] = [
	PositionHoof.Front_Left,
	PositionHoof.Front_Right,
	PositionHoof.Rear_Left,
	PositionHoof.Rear_Right,
]

const getHoofLabels = (positions: PositionHoof[]) =>
	[...positions]
		.sort((a, b) => HOOF_ORDER.indexOf(a) - HOOF_ORDER.indexOf(b))
		.map(position => getTranslationStatus(position))

const groupPositionsByKey = <
	T extends { title: string; positions: PositionHoof[] },
>(
	map: Map<string, T>,
	key: string,
	title: string,
	position?: PositionHoof,
) => {
	const existing = map.get(key)
	if (existing) {
		if (position && !existing.positions.includes(position)) {
			existing.positions.push(position)
		}
		return
	}

	map.set(key, {
		title,
		positions: position ? [position] : [],
	} as T)
}

const TreatmentPlansCell = ({
	diagnoses,
}: {
	diagnoses: ICowsList['diagnosis']
}) => {
	const plansByKey = new Map<
		string,
		{ title: string; positions: PositionHoof[] }
	>()

	diagnoses?.forEach(diagnosis => {
		;(diagnosis.treatment_plans || []).forEach(plan => {
			const title = plan.description || plan.title
			if (!title) return

			groupPositionsByKey(plansByKey, title, title, diagnosis.position)
		})
	})

	const plans = Array.from(plansByKey.entries())

	return plans.length > 0 ? (
		<Stack direction='column' gap='24px'>
			{plans.map(([key, plan]) => (
				<Box key={key} sx={{ pr: '8px' }}>
					<TypographyPrimary sx={{ overflowWrap: 'anywhere' }}>
						{plan.title}
					</TypographyPrimary>
					{plan.positions.length > 0 && (
						<Box>
							{getHoofLabels(plan.positions).map(label => (
								<TypographySecondary key={label} sx={{ display: 'block' }}>
									{label}
								</TypographySecondary>
							))}
						</Box>
					)}
				</Box>
			))}
		</Stack>
	) : (
		<TypographyPrimary>Не назначено</TypographyPrimary>
	)
}

const DiagnosesCell = ({
	diagnoses,
	diagnosesList,
}: {
	diagnoses: ICowsList['diagnosis']
	diagnosesList: { id: number; title: string }[]
}) => {
	const diagnosesByKey = new Map<
		string,
		{ title: string; positions: PositionHoof[] }
	>()

	diagnoses?.forEach(diagnose => {
		;(diagnose.sickness || []).forEach(sickness => {
			const title = (
				diagnosesList.find(item => item.id === sickness.id)?.title ||
				sickness.title ||
				''
			)
				.replace(/\(.*?\)/g, '')
				.trim()

			if (!title) return

			groupPositionsByKey(
				diagnosesByKey,
				String(sickness.id ?? title),
				title,
				diagnose.position,
			)
		})
	})

	const groupedDiagnoses = Array.from(diagnosesByKey.entries())

	if (!groupedDiagnoses.length) {
		return <TypographyPrimary>-</TypographyPrimary>
	}

	return (
		<Stack direction='column' gap='16px'>
			{groupedDiagnoses.map(([key, diagnose]) => (
				<Box key={key}>
					<TypographyPrimary sx={{ overflowWrap: 'anywhere' }}>
						{diagnose.title}
					</TypographyPrimary>
					{diagnose.positions.length > 0 && (
						<Box>
							{getHoofLabels(diagnose.positions).map(label => (
								<TypographySecondary key={label} sx={{ display: 'block' }}>
									{label}
								</TypographySecondary>
							))}
						</Box>
					)}
				</Box>
			))}
		</Stack>
	)
}

const LamenessTable = (props: LamenesTableProps) => {
	const { cattlesInfo, fetchingCowsList, activeFilters, getColumn } = props
	const dispatch = useAppDispatch()
	const { diagnosesList, diagnosesListPending } = useAppSelector(
		state => state.cattlesSlice,
	)
	useEffect(() => {
		if (diagnosesList.length === 0) {
			dispatch(fetchDiagnosesList())
		}
	}, [diagnosesList.length, dispatch])

	const containerRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		containerRef.current?.scrollTo({ top: 0, behavior: 'auto' })
	}, [cattlesInfo])

	const stickyTagSx = {
		minWidth: '130px',
		width: '130px',
		position: 'sticky',
		left: 0,
		background: '#fff',
		zIndex: 2,
	} as const

	const stickyLimpnessSx = {
		minWidth: '160px',
		width: '160px',
		position: 'sticky',
		left: 130,
		background: '#fff',
		zIndex: 2,
	} as const

	const stickyActionsSx = {
		minWidth: '113px',
		width: '113px',
		position: 'sticky',
		right: 0,
		background: '#fff',
		zIndex: 2,
	} as const

	const tableMinWidth =
		FIXED_COLUMNS_WIDTH +
		(Object.keys(COLUMN_WIDTHS) as LamenessColumnKey[]).reduce(
			(width, key) =>
				width + (getColumn(key)?.isShowColumn ? COLUMN_WIDTHS[key] : 0),
			0,
		)

	const columnWidthSx = (key: LamenessColumnKey) => ({
		minWidth: `${COLUMN_WIDTHS[key]}px`,
		paddingRight: '16px',
	})

	const visibleColumnCount =
		3 +
		(Object.keys(COLUMN_WIDTHS) as LamenessColumnKey[]).filter(
			key => getColumn(key)?.isShowColumn,
		).length

	const emptyStateMessage = activeFilters?.treatmentStatus?.statuses?.includes(
		TreatmentStatus.DisposeRecommended,
	) ? (
		<TypographySecondary14>
			Рекомендованных на выбытие коров не обнаружено
		</TypographySecondary14>
	) : activeFilters?.inspectionStatus?.statuses?.includes(
			InspectionStatus.Planned,
	  ) ? (
		<TypographySecondary14>
			Коров, планируемых на осмотр не обнаружено
		</TypographySecondary14>
	) : activeFilters?.inspectionStatus?.statuses?.includes(
			InspectionStatus.Expired,
	  ) ? (
		<TypographySecondary14>
			Коров, с просроченным осмотром не обнаружено
		</TypographySecondary14>
	) : (
		<Stack
			direction='column'
			justifyContent='center'
			alignItems='center'
			gap='8px'
		>
			<TypographySecondaryBold16>Нет данных</TypographySecondaryBold16>
			<TypographySecondary14>
				Нет данных, соответствующих выбранным условиям
			</TypographySecondary14>
		</Stack>
	)

	return (
		<TableContainer
			ref={containerRef}
			sx={{
				marginTop: '12px',
				overflowY: 'auto',
				overflowX: 'auto',
				width: '100%',
				maxWidth: '100%',
				minWidth: 0,
				height: 'calc(100vh - 350px)',
				'&::-webkit-scrollbar': {
					width: 4,
					height: 4,
				},
				'&::-webkit-scrollbar-track': {
					backgroundColor: '#DBDBDB',
					borderRadius: 12,
				},
				'&::-webkit-scrollbar-thumb': {
					backgroundColor: COLORS.icon,
					borderRadius: 12,
				},
			}}
		>
			<Table
				stickyHeader
				sx={{
					width: '100%',
					minWidth:
						!fetchingCowsList &&
						!diagnosesListPending &&
						cattlesInfo.length === 0
							? '100%'
							: `${tableMinWidth}px`,
					tableLayout: 'fixed',
				}}
			>
				<TableHead
					sx={{
						[`& .${tableCellClasses.root}`]: {
							backgroundColor: '#fff',
							paddingBottom: '12px',
						},
					}}
				>
					<TableRow sx={{ width: '100%', height: '56px' }}>
						<TableCell
							align='left'
							padding='none'
							sx={{ ...stickyTagSx, zIndex: 5 }}
						>
							<Box
								display='flex'
								flexDirection='row'
								alignItems='center'
								gap='4px'
							>
								<TypographySecondary>
									Номер бирки
									<br />
									Номер ошейника
								</TypographySecondary>
							</Box>
						</TableCell>
						<TableCell
							align='left'
							padding='none'
							sx={{ ...stickyLimpnessSx, zIndex: 5 }}
						>
							<TypographySecondary>
								Хромота, балл
								<br />
								Продолжительность
							</TypographySecondary>
						</TableCell>
						{getColumn(LamenessColumnKey.TREATMENT_PLAN)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.TREATMENT_PLAN)}
							>
								<TypographySecondary>Схема лечения</TypographySecondary>
							</TableCell>
						)}
						{getColumn(LamenessColumnKey.DIAGNOSIS)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.DIAGNOSIS)}
							>
								<TypographySecondary>Диагноз</TypographySecondary>
							</TableCell>
						)}
						{getColumn(LamenessColumnKey.TREATMENT_STATUS)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.TREATMENT_STATUS)}
							>
								<TypographySecondary>Статус лечения</TypographySecondary>
							</TableCell>
						)}
						{getColumn(LamenessColumnKey.INSPECTION_DATE)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.INSPECTION_DATE)}
							>
								<TypographySecondary>Дата осмотра</TypographySecondary>
							</TableCell>
						)}
						{getColumn(LamenessColumnKey.LACTATION_DAY)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.LACTATION_DAY)}
							>
								<TypographySecondary>
									День
									<br />
									лактации
								</TypographySecondary>
							</TableCell>
						)}
						{getColumn(LamenessColumnKey.COW_AGE)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.COW_AGE)}
							>
								<TypographySecondary>Возраст коровы</TypographySecondary>
							</TableCell>
						)}
						{getColumn(LamenessColumnKey.GROUP)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.GROUP)}
							>
								<TypographySecondary>Группа</TypographySecondary>
							</TableCell>
						)}
						{getColumn(LamenessColumnKey.LACTATION_STATUS)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.LACTATION_STATUS)}
							>
								<TypographySecondary>Статус лактации</TypographySecondary>
							</TableCell>
						)}
						{getColumn(LamenessColumnKey.FATNESS)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.FATNESS)}
							>
								<TypographySecondary>Упитанность, балл</TypographySecondary>
							</TableCell>
						)}
						{getColumn(LamenessColumnKey.EXAMINER)?.isShowColumn && (
							<TableCell
								align='left'
								padding='none'
								sx={columnWidthSx(LamenessColumnKey.EXAMINER)}
							>
								<TypographySecondary>Сотрудник</TypographySecondary>
							</TableCell>
						)}
						<TableCell
							align='right'
							padding='none'
							sx={{ ...stickyActionsSx, zIndex: 5 }}
						>
							<Box mr='30px'>
								<TypographySecondary>Действия</TypographySecondary>
							</Box>
						</TableCell>
					</TableRow>
				</TableHead>
				{fetchingCowsList || diagnosesListPending ? (
					<TableBody>
						<TableRow>
							<TableCell
								colSpan={visibleColumnCount}
								align={'center'}
								padding={'none'}
							>
								<TableSkeleton
									widthForBody={'100%'}
									heightForBody={'34px'}
									rowCount={36}
									totalHeight={'calc(100vh - 350px)'}
								/>
							</TableCell>
						</TableRow>
					</TableBody>
				) : cattlesInfo.length === 0 ? (
					<TableBody>
						<TableRow
							sx={{
								width: '100%',
								[`& .${tableCellClasses.root}`]: {
									borderBottom: 'none',
									height: '400px',
								},
							}}
						>
							<TableCell
								colSpan={visibleColumnCount}
								align={'center'}
								padding={'none'}
								sx={{ width: '100%' }}
							>
								{emptyStateMessage}
							</TableCell>
						</TableRow>
					</TableBody>
				) : (
					<TableBody>
						{cattlesInfo.map((el, index) => (
							<TableRow
								key={index}
								sx={{
									height: '46px',
									// '&:hover': {
									// 	background: COLORS.mainSecondaryOpacity,
									// },
									[`& .${tableCellClasses.root}`]: {
										borderBottom: 'none',
									},
								}}
							>
								<TableCell
									align='left'
									padding='none'
									sx={{
										verticalAlign: 'top',
										padding: '8px 0px',
										...stickyTagSx,
									}}
								>
									<Box display='flex' flexDirection='column'>
										<TypographyPrimary>{el.tag_id}</TypographyPrimary>
										{el.collar_num && (
											<TypographySecondary14>
												{el.collar_num}
											</TypographySecondary14>
										)}
									</Box>
								</TableCell>
								<TableCell
									align='left'
									padding='none'
									sx={{
										verticalAlign: 'top',
										padding: '8px 0px',
										...stickyLimpnessSx,
									}}
								>
									{el.limpness != null ? (
										<TypographyPrimary>
											{el.limpness}{' '}
											<span
												style={{ fontSize: '12px', color: COLORS.secondary }}
											>
												{morphForWords(el.limpness, [
													'балл',
													'балла',
													'баллов',
												])}{' '}
												<br /> {el.limpness_length ? el.limpness_length : '-'}
											</span>
										</TypographyPrimary>
									) : (
										<TypographySecondary>Нет данных</TypographySecondary>
									)}
								</TableCell>
								{getColumn(LamenessColumnKey.TREATMENT_PLAN)?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 16px 8px 0px',
											overflowWrap: 'anywhere',
										}}
									>
										<TreatmentPlansCell diagnoses={el.diagnosis} />
									</TableCell>
								)}
								{getColumn(LamenessColumnKey.DIAGNOSIS)?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 16px 8px 0px',
											overflowWrap: 'anywhere',
										}}
									>
										<DiagnosesCell
											diagnoses={el.diagnosis}
											diagnosesList={diagnosesList}
										/>
									</TableCell>
								)}
								{getColumn(LamenessColumnKey.TREATMENT_STATUS)
									?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 16px 8px 0px',
											overflowWrap: 'anywhere',
										}}
									>
										{[
											TreatmentStatus.NotPrescribed,
											TreatmentStatus.Treated,
											TreatmentStatus.Completed,
											TreatmentStatus.DisposeRecommended,
										].includes(el.treatment_status) ? (
											<>
												<TypographyPrimary
													sx={
														el.treatment_status ===
														TreatmentStatus.DisposeRecommended
															? { color: COLORS.error }
															: undefined
													}
												>
													{el.treatment_status ===
													TreatmentStatus.DisposeRecommended
														? 'На выбытие'
														: getTranslationStatus(el.treatment_status)}
												</TypographyPrimary>
												{el.treatment_status ===
												TreatmentStatus.DisposeRecommended ? (
													el.first_examination_date ? (
														<TypographySecondary>
															{moment(el.first_examination_date).format(
																'DD.MM.YY',
															)}
														</TypographySecondary>
													) : null
												) : (
													el.treatment_length && (
														<TypographySecondary>
															{el.treatment_length}
														</TypographySecondary>
													)
												)}
											</>
										) : (
											<TypographyPrimary>-</TypographyPrimary>
										)}
									</TableCell>
								)}
								{getColumn(LamenessColumnKey.INSPECTION_DATE)?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 0px',
										}}
									>
										{/*
										{el.examination_status && (
											<>
												<TypographyPrimary sx={{ color: el.treatment_color }}>
													{getTranslationStatus(el.examination_status)}
												</TypographyPrimary>
												{el.next_examination_date && (
													<TypographySecondary
														sx={{ color: el.treatment_color }}
													>
														{moment(el.next_examination_date).format(
															'DD.MM.YYYY',
														)}
													</TypographySecondary>
												)}


											</>
										)}
											*/}
										{el.next_examination_date ? (
											<TypographyPrimary
												sx={{
													color: moment(el.next_examination_date).isBefore(
														moment(),
														'day',
													)
														? COLORS.error
														: COLORS.black,
												}}
											>
												{moment(el.next_examination_date).format('DD.MM.YY')}
											</TypographyPrimary>
										) : (
											<TypographyPrimary sx={{ color: COLORS.secondaryFont }}>
												-
											</TypographyPrimary>
										)}
									</TableCell>
								)}
								{getColumn(LamenessColumnKey.LACTATION_DAY)?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 0px',
										}}
									>
										<TypographySecondary14>
											{el.lactation_length +
												' ' +
												morphForWords(el.lactation_length, [
													'дней',
													'день',
													'дня',
												])}
										</TypographySecondary14>
									</TableCell>
								)}
								{getColumn(LamenessColumnKey.COW_AGE)?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 0px',
										}}
									>
										<TypographyPrimary>
											{getYearsOld(el.birth_date)}
										</TypographyPrimary>
									</TableCell>
								)}
								{getColumn(LamenessColumnKey.GROUP)?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 0px',
										}}
									>
										<TypographyPrimary>{el.group.title}</TypographyPrimary>
									</TableCell>
								)}
								{getColumn(LamenessColumnKey.LACTATION_STATUS)
									?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 0px',
										}}
									>
										<TypographyPrimary>{el.lactation_state}</TypographyPrimary>
									</TableCell>
								)}
								{getColumn(LamenessColumnKey.FATNESS)?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 0px',
										}}
									>
										{el.fatness != null ? (
											<TypographyPrimary>
												{el.fatness +
													' ' +
													morphForWords(el.fatness, [
														'балл',
														'балла',
														'баллов',
													])}
											</TypographyPrimary>
										) : (
											<TypographySecondary>Нет данных</TypographySecondary>
										)}
									</TableCell>
								)}
								{getColumn(LamenessColumnKey.EXAMINER)?.isShowColumn && (
									<TableCell
										align='left'
										padding='none'
										sx={{
											verticalAlign: 'top',
											padding: '8px 0px',
										}}
									>
										<TypographyPrimary>
											{el.examiner && el.examiner.trim() !== ''
												? el.examiner
												: '-'}
										</TypographyPrimary>
									</TableCell>
								)}
								<TableCell
									align='right'
									padding='none'
									sx={{
										verticalAlign: 'top',
										padding: '8px 0px',
										...stickyActionsSx,
									}}
								>
									<Box
										display={'flex'}
										flexDirection={'row'}
										alignItems={'center'}
										gap={'4px'}
										justifyContent={'flex-end'}
										width='fit-content'
										ml='auto'
										mr={'30px'}
									>
										<TooltipHover
											title='Провести осмотр'
											placement='bottom-end'
											oneLine
										>
											<Box
												onClick={() =>
													openCheckupInNewTab(
														`/cattles/lameness/${encodeURIComponent(
															String(el.tag_id),
														)}/check-up`,
													)
												}
											>
												<CheckupIcon
													stroke={COLORS.secondaryFont}
													style={{ cursor: 'pointer' }}
												/>
											</Box>
										</TooltipHover>
										<TooltipHover
											title='Перейти на страницу коровы'
											placement='bottom-start'
											oneLine
										>
											<ArrowNext
												onClick={() =>
													openInNewBrowserTab(
														`/cattles/cow/${encodeURIComponent(
															String(el.tag_id),
														)}`,
													)
												}
												style={{
													cursor: 'pointer',
													transform: 'rotate(180deg)',
												}}
												stroke={COLORS.secondary}
											/>
										</TooltipHover>
									</Box>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				)}
			</Table>
		</TableContainer>
	)
}

export default LamenessTable
